import { useState, FormEvent, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import { useCurrencies } from "../../../context/CurrenciesContext";
import styles from "./SearchBar.module.scss";
import SearchIcon from "../../../assets/svgs/header/SearchIcon";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../../../hooks/useDebounce";
import ProductsService from "../../../api/services/products.service";
import { ProductsTypes } from "../../../api/types/products.types";
import { useLocalizedNavigate } from "../../../hooks/useLocalizedNavigate";

const SearchBar = () => {
  const { t } = useTranslation("translation");
  const navigate = useNavigate();
  const localizedNavigate = useLocalizedNavigate();
  const { currentLanguage } = useLanguage();
  const { currencyId } = useCurrencies();
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<ProductsTypes[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const debouncedQuery = useDebounce(searchQuery, 300);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const getLocalizedName = (product: ProductsTypes) => {
    const langKey = `name_${currentLanguage}` as keyof ProductsTypes;
    const name = product[langKey];
    if (name && typeof name === "string") return name;
    return product.name_en || product.name_az || "";
  };

  // Fetch autocomplete suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!debouncedQuery.trim() || debouncedQuery.length < 2 || !currencyId) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setLoading(true);
      try {
        const res = await ProductsService.searchProducts(
          debouncedQuery,
          currencyId,
          currentLanguage,
          1,
          5, // Limit to 5 suggestions
        );

        const variants = (res.data.data || []) as ProductsTypes[];
        setSuggestions(variants);
        setShowSuggestions(variants.length > 0);
        setSelectedIndex(-1);
      } catch (error) {
        console.error("Autocomplete error:", error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [debouncedQuery, currencyId, currentLanguage]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault();
        if (searchQuery.trim()) {
          setShowSuggestions(false);
          navigate(
            `/${currentLanguage}/search?q=${encodeURIComponent(searchQuery.trim())}`,
          );
        }
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else if (searchQuery.trim()) {
          setShowSuggestions(false);
          navigate(
            `/${currentLanguage}/search?q=${encodeURIComponent(searchQuery.trim())}`,
          );
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Scroll selected suggestion into view
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionsRef.current) {
      const selectedElement = suggestionsRef.current.children[
        selectedIndex
      ] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedIndex]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSuggestions(false);
      navigate(
        `/${currentLanguage}/search?q=${encodeURIComponent(searchQuery.trim())}`,
      );
    }
  };

  const handleSuggestionClick = useCallback(
    (product: ProductsTypes) => {
      setShowSuggestions(false);
      setSearchQuery("");
      localizedNavigate(`/product/${product.id}`);
    },
    [localizedNavigate],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setSelectedIndex(-1);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <div className={styles.search_wrapper} ref={searchBarRef}>
      <form className={styles.search_bar} onSubmit={handleSubmit}>
        <input
          type="text"
          className={styles.search_input}
          placeholder={t("search_product") || ""}
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
        />
        <button type="submit" className={styles.search_btn}>
          <SearchIcon />
        </button>
      </form>

      {showSuggestions && (
        <div className={styles.suggestions} ref={suggestionsRef}>
          {loading ? (
            <div className={styles.suggestion_item}>
              <p>{t("loading") || "Loading..."}</p>
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((product, index) => {
              const name = getLocalizedName(product);
              const displayPrice = product.discount_price
                ? Number(product.discount_price).toFixed(2)
                : Number(product.price).toFixed(2);
              const currency = product.currency_name || "";

              return (
                <div
                  key={product.id}
                  className={`${styles.suggestion_item} ${
                    index === selectedIndex ? styles.selected : ""
                  }`}
                  onClick={() => handleSuggestionClick(product)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {product.images && product.images[0] && (
                    <img
                      src={product.images[0].image}
                      alt={name}
                      className={styles.suggestion_image}
                    />
                  )}
                  <div className={styles.suggestion_info}>
                    <p className={styles.suggestion_name}>{name}</p>
                    <p className={styles.suggestion_price}>
                      {displayPrice} {currency}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <div className={styles.suggestion_item}>
              <p>{t("no_search_results") || "No results found"}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
